/**
 * Mappers para convertir entre DTOs y modelos de dominio de Contract
 */

import {
  BudgetModeDto,
  ClientPartyDto,
  ContractPartyDto,
  CreateContractPayloadDto,
  CreateContractResponseDto,
  ContractorDto,
  ContractDto
} from '@infra/dto/contract.dto';
import {
  BudgetMode,
  ClientParty,
  ContractParty,
  Contractor,
  Contract,
  CreateContractResponse
} from '@domain/models/contract.model';

export class CreateContractResponseMapper {
  static toDomain(dto: CreateContractResponseDto): CreateContractResponse {
    return {
      contractId: dto.contractId || '',
      projectId: dto.projectId || '',
      message: dto.message || ''
    };
  }
}

export class BudgetModeMapper {
  static toDomain(dto: BudgetModeDto): BudgetMode {
    return {
      id: dto.budgetModeId,
      name: dto.name,
      description: dto.description,
      isMaterialIncluded: dto.isMaterialIncluded
    };
  }

  static toDomainList(dtos: BudgetModeDto[]): BudgetMode[] {
    return dtos.map(dto => BudgetModeMapper.toDomain(dto));
  }
}

export class ClientPartyMapper {
  static toDto(party: ClientParty): ClientPartyDto {
    return {
      fullName: party.fullName,
      documentType: party.document.type,
      documentNumber: party.document.number
    };
  }

  static toDomain(dto: ClientPartyDto): ClientParty {
    return {
      fullName: dto.fullName,
      document: {
        type: dto.documentType,
        number: dto.documentNumber
      }
    };
  }
}

export class ContractPartyMapper {
  static toDomain(dto: ContractPartyDto): ContractParty {
    return {
      id: dto.partyId,
      contractId: dto.contractId,
      fullName: dto.fullName,
      document: {
        type: dto.documentType,
        number: dto.documentNumber
      }
    };
  }

  static toDomainList(dtos: ContractPartyDto[]): ContractParty[] {
    return dtos.map(dto => ContractPartyMapper.toDomain(dto));
  }
}

export class ContractorDtoMapper {
  static toDomain(dto: ContractorDto): Contractor {
    return {
      fullName: dto.fullName,
      dni: dto.dni,
      phoneNumber: dto.phoneNumber
    };
  }
}

export class ContractMapper {
  /**
   * Convierte un DTO de Contract a modelo de dominio
   */
  static toDomain(dto: ContractDto): Contract {
    return {
      id: dto.contractId,
      contractNumber: dto.contractNumber,
      contractorId: dto.contractorId,
      contractor: dto.contractor ? ContractorDtoMapper.toDomain(dto.contractor) : undefined,
      projectId: dto.projectId,
      status: {
        id: dto.statusId,
        name: dto.statusName
      },
      project: {
        id: dto.projectId,
        name: dto.projectName,
        address: dto.projectAddress,
        district: dto.projectDistrict
      },
      dates: {
        start: new Date(dto.startDate),
        estimatedEnd: new Date(dto.estimatedEndDate),
        createdAt: new Date(dto.createdAt),
        updatedAt: dto.updatedAt ? new Date(dto.updatedAt) : undefined
      },
      scope: {
        metric: dto.scopeMetric,
        description: dto.scopeDescription
      },
      constructionPlanUrl: dto.constructionPlanUrl,
      budget: {
        modeId: dto.budgetModeId,
        laborCost: dto.laborCost,
        materialCost: dto.materialCost,
        materialEstimate: {
          min: dto.materialEstimateMin,
          max: dto.materialEstimateMax
        },
        totalAmount: dto.totalAmount
      },
      advancePayment: dto.hasAdvancePayment ? {
        hasAdvance: dto.hasAdvancePayment,
        amount: dto.advanceAmount,
        proofUrl: dto.advanceProofUrl
      } : undefined,
      parties: {
        client: dto.clientParty ? ClientPartyMapper.toDomain(dto.clientParty) : undefined,
        contractParties: dto.contractParties ? ContractPartyMapper.toDomainList(dto.contractParties) : undefined
      }
    };
  }

  /**
   * Convierte un modelo de dominio Contract a DTO para crear contrato
   */
  static createPayloadToDto(contract: Partial<Contract>): CreateContractPayloadDto {
    if (!contract.project || !contract.dates || !contract.scope || !contract.budget || !contract.parties?.client) {
      throw new Error('Missing required fields to create contract');
    }

    return {
      contractorId: contract.contractorId || '',
      projectId: contract.project.id,
      projectName: contract.project.name,
      projectAddress: contract.project.address,
      projectDistrict: contract.project.district,
      startDate: contract.dates.start.toISOString(),
      estimatedEndDate: contract.dates.estimatedEnd.toISOString(),
      scopeMetric: contract.scope.metric,
      scopeDescription: contract.scope.description,
      constructionPlanUrl: contract.constructionPlanUrl || null,
      budgetModeId: contract.budget.modeId,
      laborCost: contract.budget.laborCost,
      materialCost: contract.budget.materialCost || 0,
      materialEstimateMin: contract.budget.materialEstimate?.min || null,
      materialEstimateMax: contract.budget.materialEstimate?.max || null,
      totalAmount: contract.budget.totalAmount,
      hasAdvancePayment: contract.advancePayment?.hasAdvance || false,
      advanceAmount: contract.advancePayment?.amount || 0,
      advanceProofUrl: contract.advancePayment?.proofUrl || null,
      clientParty: ClientPartyMapper.toDto(contract.parties.client)
    };
  }

  /**
   * Convierte un modelo de dominio Contract a DTO (para respuestas)
   */
  static toDto(contract: Contract): ContractDto {
    return {
      contractId: contract.id,
      contractNumber: contract.contractNumber,
      contractorId: contract.contractorId,
      contractor: contract.contractor ? {
        fullName: contract.contractor.fullName,
        dni: contract.contractor.dni,
        phoneNumber: contract.contractor.phoneNumber
      } : undefined,
      projectId: contract.projectId,
      statusId: contract.status.id,
      statusName: contract.status.name,
      createdAt: contract.dates.createdAt.toISOString(),
      updatedAt: contract.dates.updatedAt?.toISOString() || null,
      projectName: contract.project.name,
      projectAddress: contract.project.address,
      projectDistrict: contract.project.district || '',
      startDate: contract.dates.start.toISOString(),
      estimatedEndDate: contract.dates.estimatedEnd.toISOString(),
      scopeMetric: contract.scope.metric,
      scopeDescription: contract.scope.description,
      constructionPlanUrl: contract.constructionPlanUrl || null,
      budgetModeId: contract.budget.modeId,
      laborCost: contract.budget.laborCost,
      materialCost: contract.budget.materialCost || null,
      materialEstimateMin: contract.budget.materialEstimate?.min || null,
      materialEstimateMax: contract.budget.materialEstimate?.max || null,
      totalAmount: contract.budget.totalAmount,
      hasAdvancePayment: contract.advancePayment?.hasAdvance || false,
      advanceAmount: contract.advancePayment?.amount || 0,
      advanceProofUrl: contract.advancePayment?.proofUrl || null,
      contractParties: contract.parties?.contractParties?.map(p => ({
        partyId: p.id,
        contractId: p.contractId,
        fullName: p.fullName,
        documentType: p.document.type,
        documentNumber: p.document.number
      })),
      clientParty: contract.parties?.client ? ClientPartyMapper.toDto(contract.parties.client) : undefined
    };
  }
}

